import type { RecruitmentStatusResponse, RpmResponse } from '@on3/api';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { Check } from '@on3/ui-lib/components/Svg';
import { useSite } from '@on3/ui-lib/contexts/SiteProvider';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { createUrl } from '@on3/ui-lib/utils/url';
import { SlimTransferStatusV1 } from '@public/components/RecruitmentStatus/SlimTransferStatusV1';
import { Text } from '@public/components/UI/Typography/Text';

import styles from './PlayerRankingsStatus.module.scss';

interface IPlayerRankingItemStatusProps {
  status?: RecruitmentStatusResponse;
  slug?: string | null;
  sport?: string | null;
  predictions?: RpmResponse[] | null;
  recruitmentKey?: number | null;
  year?: string | null;
}
export const PlayerRankingItemStatus = ({
  status,
  slug,
  predictions,
  recruitmentKey,
  sport,
  year,
}: IPlayerRankingItemStatusProps) => {
  const {
    isCommitted,
    isSigned,
    isEnrolled,
    isTransfer,
    interestCount,
    committedOrganizationSlug,
  } = status || {};

  const hasStatus = isCommitted || isEnrolled || isSigned;

  const hasPredictions = !!predictions?.length;
  const { sportsData } = useSite();

  const isIndustrySport = sportsData?.find(
    (sportData) => slugify(sportData.name) === sport,
  )?.isIndustryRankable;

  const commitLink = isIndustrySport
    ? 'industry-comparison-commits'
    : 'commits';

  return (
    <div className={styles.statusWrapper}>
      {hasStatus && !isTransfer && (
        <div className={styles.statusContainer}>
          <Link
            className={styles.statusOrg}
            href={createUrl([
              'college',
              committedOrganizationSlug,
              sport,
              year,
              commitLink,
            ])}
          >
            <figure>
              <Avatar
                domain={status?.committedOrganizationAsset?.domain}
                height={30}
                source={status?.committedOrganizationAsset?.source}
                teamAvatar
                width={30}
              />
            </figure>
            {(isEnrolled || isSigned) && (
              <div className={styles.checkmarkContainer}>
                <Check className={styles.checkmarkIcon} />
              </div>
            )}
          </Link>
          {hasStatus && (
            <span className={styles.signed}>
              {isEnrolled ? 'Enrolled' : isSigned ? 'Signed' : 'Commit'}
            </span>
          )}
        </div>
      )}
      {!hasStatus && hasPredictions && (
        <Link
          className={styles.predictionsContainer}
          href={createUrl(['db', slug, 'recruiting', recruitmentKey, 'rpm'])}
        >
          {predictions?.slice(0, 2).map((org) => {
            return (
              <div className={styles.prediction} key={org.key}>
                <figure>
                  <Avatar
                    domain={org?.organization?.asset?.domain}
                    height={20}
                    source={org?.organization?.asset?.source}
                    teamAvatar
                    type={org?.organization?.asset?.type}
                    width={20}
                  />
                </figure>
                {org.percent && (
                  <Text className={styles.rpmPercent}>
                    {Math.round(org.percent)}%
                  </Text>
                )}
              </div>
            );
          })}
        </Link>
      )}
      {!hasStatus && !hasPredictions && (
        <Link
          className={styles.totalInterest}
          href={`/rivals/${slug}/recruiting/`}
        >
          <span className={styles.interestCount}>{interestCount}</span>
          Interests
        </Link>
      )}
      {status && isTransfer && <SlimTransferStatusV1 status={status} />}
    </div>
  );
};
